import { apiBaseUrl, isApiMode } from './baseUrl';
import { loadDeploymentConfig } from './deploymentConfig';
import { retryAfterSeconds } from './retryAfter';

// ─────────────────────────────────────────────────────────────────────
// THE DEV-ONLY STAFF SIGN-IN (#445).
//
// `/sa` normally authenticates through Entra. That flow is popup-based and
// does not survive every development browser, which blocks ordinary portal
// testing on dev. This is an alternate way to AUTHENTICATE one seeded staff
// identity — not a way to obtain authority. The session it returns is an
// ordinary one, and `/sa` applies the same platform RBAC to it.
//
// A PLAIN FETCH rather than `apiFetch`, like every other sign-in: there is no
// session yet to send, and a stale one must not ride along.
//
// ── BUT `credentials: 'include'`, AND IT IS NOT OPTIONAL (#839 review) ──
//
// This file previously said «this door sets no cookie», and #754 stage 2 made
// that false: the verify endpoint now answers with `Set-Cookie`, because
// `scripts/dev-login.mjs --staff` needs it and because a reload needs it. The
// comment stayed, and a plain `fetch` defaults to `credentials: 'same-origin'`
// — so on dev, where the app is `dev.…` and the API is `api-dev.…`, THE
// BROWSER DISCARDED THE COOKIE.
//
// The symptom was invisible at the moment of sign-in: `clubSession.adopt`
// keeps the bearer token in memory, so `/sa` rendered correctly and only a
// RELOAD revealed that nothing had been stored. Confirmed in a real browser
// against a local API — zero `sps_session` cookies in the jar, and the sign-in
// form back after a refresh. `lib/api/accountSignIn.ts` sets this flag for
// exactly the same reason.
//
// THE DEPLOYMENT DECIDES WHETHER THIS EXISTS. The endpoint is mounted only
// where `DEV_STAFF_LOGIN=enabled`, and `/config` reports it — one control, on
// the server, rather than a build flag that could drift from it.
//
// ── IT IS A CODE DOOR NOW, NOT A PASSWORD ONE (#754 stage 2) ───────
//
// Two steps, because the credential is mailed: `request` then `verify`. The
// endpoints are the staff ones, NOT `/auth/otp` — the customer entrance
// provisions an account for any address, which is precisely what a door into
// `/sa` must never do. Separate paths, separate purpose, separate budget.
// ─────────────────────────────────────────────────────────────────────

export interface DevStaffSession {
  token: string;
  expiresAt: string;
  account: { id: string; displayName: string | null; email: string | null };
}

/**
 * Asking for a code.
 *
 * `sent` MEANS «THE REQUEST GOT THROUGH», NOT «THAT ADDRESS EXISTS». The
 * endpoint answers 202 for an unknown address, the real one and a disabled
 * account alike, so that it cannot be polled to find out which address the dev
 * staff one is. Reporting anything finer here would rebuild that oracle on the
 * client after the server refused to be one.
 */
export type DevStaffCodeRequest =
  { state: 'sent' } | { state: 'throttled'; retryAfterSeconds: number } | { state: 'unavailable' };

export type DevStaffLoginResult =
  | { state: 'ok'; session: DevStaffSession }
  /** Wrong address, wrong or expired code, or an account that cannot sign in. */
  | { state: 'refused' }
  /** Too many attempts from here. */
  | { state: 'throttled'; retryAfterSeconds: number }
  /** The form is not offered by this deployment, or the request failed. */
  | { state: 'unavailable' };

/** Does this deployment offer the dev form at all? */
export async function devStaffLoginAvailable(): Promise<boolean> {
  return (await loadDeploymentConfig()).staffLogin.devFormEnabled;
}

export async function requestDevStaffCode(
  email: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<DevStaffCodeRequest> {
  if (!isApiMode()) return { state: 'unavailable' };
  try {
    const res = await fetchImpl(apiBaseUrl() + '/auth/dev/staff/otp/request', {
      method: 'POST',
      // Nothing to receive here — this step mints a challenge and sets no
      // cookie — but the two steps use one credentials mode on purpose: a
      // reader comparing them should not have to work out whether the
      // difference means something.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (res.status === 429)
      return { state: 'throttled', retryAfterSeconds: retryAfterSeconds(res) };
    // 202 is the ordinary answer; 200 would do as well. Anything else — 404
    // from a deployment that does not offer this, 503 from one with no mailer
    // — is «no door here», which is what the form says.
    if (res.ok || res.status === 202) return { state: 'sent' };
    return { state: 'unavailable' };
  } catch {
    return { state: 'unavailable' };
  }
}

export async function devStaffLogin(
  email: string,
  code: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<DevStaffLoginResult> {
  if (!isApiMode()) return { state: 'unavailable' };
  try {
    const res = await fetchImpl(apiBaseUrl() + '/auth/dev/staff/otp/verify', {
      method: 'POST',
      // THE SESSION COOKIE ARRIVES ON THIS RESPONSE. Without this the browser
      // drops it cross-origin and `/sa` cannot survive a reload. See the header.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (res.ok) {
      const session = (await res.json()) as DevStaffSession;
      return session?.token ? { state: 'ok', session } : { state: 'unavailable' };
    }
    if (res.status === 429)
      return { state: 'throttled', retryAfterSeconds: retryAfterSeconds(res) };
    // 401 and 403 are both «this did not sign you in», and the form says the
    // same thing for each: the server deliberately does not distinguish a
    // wrong code from an unknown account, and neither should this.
    if (res.status === 401 || res.status === 403) return { state: 'refused' };
    return { state: 'unavailable' };
  } catch {
    return { state: 'unavailable' };
  }
}
